import type { FormEvent, ReactNode } from "react";
import { ThemeToggle } from "@/components/ThemeToggle";

interface AuthCardProps {
  title: string;
  subtitle?: ReactNode;
  onSubmit?: (e: FormEvent) => void;
  children: ReactNode;
}

/** The centred card the pre-login pages share (signup, verify, finish). */
export function AuthCard({ title, subtitle, onSubmit, children }: AuthCardProps) {
  return (
    <div className="canvas-ambient relative flex min-h-screen items-center justify-center bg-canvas px-4">
      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>
      <form
        onSubmit={onSubmit ?? ((e) => e.preventDefault())}
        className="relative w-full max-w-sm overflow-hidden rounded-xl border border-line-subtle bg-surface p-8 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.45)] space-y-6"
      >
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-electric-blue/60 to-transparent"
        />
        <div className="text-center space-y-2">
          <img src="/phantm-logo.png" alt="Phantm" className="mx-auto h-12 w-12 object-contain" />
          <h1 className="pt-1 text-xl font-semibold tracking-tight text-fg">{title}</h1>
          {subtitle && <p className="text-sm text-fg-muted">{subtitle}</p>}
        </div>
        {children}
      </form>
    </div>
  );
}

export const authInputCls =
  "w-full rounded-md border border-line bg-canvas px-3 py-2 text-sm text-fg placeholder:text-fg-faint focus:border-electric-blue focus:outline-none focus:ring-2 focus:ring-electric-blue/40";
export const authLabelCls = "text-[10px] font-medium uppercase tracking-[0.14em] text-fg-faint";
export const authButtonCls =
  "w-full rounded-md bg-electric-blue px-4 py-2 text-sm font-medium text-white transition-all hover:bg-electric-blue/90 focus:outline-none focus:ring-2 focus:ring-electric-blue/50 disabled:cursor-not-allowed disabled:opacity-40";

export function AuthError({ message }: { message: string }) {
  if (!message) return null;
  return (
    <div role="alert" className="rounded-md border border-block-red/30 bg-block-red/10 px-3 py-2 text-sm text-block-red">
      {message}
    </div>
  );
}
